import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Plus, Minus } from "lucide-react";
import { SERVICES, ADD_ONS, PHONE_TEL } from "@/data/spa";
import { useLang } from "@/i18n/LanguageContext";
import { AmbientMedia } from "@/components/spa/AmbientMedia";
import { TiltCard } from "@/components/spa/TiltCard";

const ServiceRow = ({ service, tr, t, isOpen, onToggle, rowRef }) => (
  <div ref={rowRef} data-testid={`service-${service.id}`} className="border-t border-white/10 last:border-b">
    <button
      onClick={onToggle}
      data-testid={`service-toggle-${service.id}`}
      className="group w-full flex items-center gap-6 py-8 lg:py-10 text-left px-2 hover:bg-white/[0.02] transition-colors duration-300"
    >
      <span className="font-serif-display italic text-lg text-[#c5a059] w-10 shrink-0">{service.number}</span>
      <div className="flex-1 min-w-0">
        <span className="block text-[10px] tracking-[0.3em] uppercase text-[#8a9690] mb-1">{tr.tag}</span>
        <span className="font-serif-display text-2xl sm:text-3xl lg:text-4xl text-[#f0f4f2] group-hover:text-[#dcc286] transition-colors duration-300 leading-tight">
          {tr.name}
        </span>
      </div>
      <span className="hidden sm:block text-sm text-[#8a9690] font-light shrink-0">
        {t.services.from} {service.pricing[0].price}
      </span>
      <span className={`shrink-0 w-11 h-11 rounded-full border flex items-center justify-center transition-all duration-500 ${isOpen ? "bg-[#c5a059] border-[#c5a059] text-[#050706] rotate-180" : "border-white/20 text-[#8a9690] group-hover:border-[#c5a059] group-hover:text-[#c5a059]"}`}>
        {isOpen ? <Minus size={16} /> : <Plus size={16} />}
      </span>
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden"
        >
          <div className="grid lg:grid-cols-12 gap-10 px-2 pb-12 lg:pl-16">
            <div className="lg:col-span-4 overflow-hidden h-64 lg:h-auto">
              <motion.div
                initial={{ scale: 1.15 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                className="w-full h-full"
              >
                <AmbientMedia
                  video={service.video}
                  poster={service.image}
                  alt={tr.name}
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </div>
            <div className="lg:col-span-4 space-y-6">
              <p className="text-sm text-[#8a9690] font-light leading-relaxed">{tr.description}</p>
              <div>
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#c5a059]">{t.services.wellSuited}</span>
                <p className="mt-2 text-sm text-[#f0f4f2]/80 font-light leading-relaxed">{tr.suitedFor}</p>
              </div>
              <div>
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#c5a059]">{t.services.theExperience}</span>
                <p className="mt-2 text-sm text-[#f0f4f2]/80 font-light leading-relaxed">{tr.experience}</p>
              </div>
            </div>
            <div className="lg:col-span-4">
              <div className="space-y-0">
                {service.pricing.map((p) => (
                  <div key={p.duration} className="flex items-baseline gap-3 py-2.5 border-b border-white/5">
                    <span className="text-sm text-[#8a9690] font-light">{p.duration.split(" ")[0]} {t.services.minutes}</span>
                    <span className="flex-1 border-b border-dotted border-white/15 -translate-y-1" />
                    <span className="font-serif-display text-xl text-[#c5a059]">{p.price}</span>
                  </div>
                ))}
              </div>
              <a
                href={PHONE_TEL}
                data-testid={`service-book-${service.id}`}
                className="mt-8 inline-flex items-center gap-3 border border-[#c5a059]/50 text-[#c5a059] hover:bg-[#c5a059] hover:text-[#050706] px-6 py-3 text-xs tracking-[0.2em] uppercase transition-colors duration-300"
              >
                <Phone size={13} /> {t.services.callToBook}
              </a>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export const Services = () => {
  const { t } = useLang();
  const [open, setOpen] = useState("deep-tissue");
  const openRef = useRef("deep-tissue");
  const overrideUntilRef = useRef(0);
  const rowRefs = useRef({});

  // Auto-open the service row most centered in the viewport.
  // Uses IntersectionObserver — reliable with Lenis smooth-scroll (native scroll events are throttled).
  useEffect(() => {
    const observers = [];
    const ratios = {};
    const pick = () => {
      if (Date.now() < overrideUntilRef.current) return;
      let bestId = null;
      let best = 0;
      for (const s of SERVICES) {
        const r = ratios[s.id] || 0;
        if (r > best) {
          best = r;
          bestId = s.id;
        }
      }
      if (bestId && bestId !== openRef.current && best > 0.15) {
        openRef.current = bestId;
        setOpen(bestId);
      }
    };
    for (const s of SERVICES) {
      const el = rowRefs.current[s.id];
      if (!el) continue;
      const io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) ratios[s.id] = e.intersectionRatio;
          pick();
        },
        {
          // Sweet-spot band around viewport center: ignore top/bottom 35% of viewport
          rootMargin: "-35% 0px -35% 0px",
          threshold: [0, 0.25, 0.5, 0.75, 1],
        }
      );
      io.observe(el);
      observers.push(io);
    }
    return () => observers.forEach((o) => o.disconnect());
  }, []);

  const handleToggle = (id) => {
    overrideUntilRef.current = Date.now() + 4000;
    const next = open === id ? null : id;
    openRef.current = next;
    setOpen(next);
  };

  return (
    <section id="services" data-testid="services-section" className="relative py-24 lg:py-40 overflow-hidden">
      <span aria-hidden="true" className="ghost-char top-6 right-2">療</span>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-20"
        >
          <div className="max-w-2xl">
            <div className="flex items-center gap-4 mb-6">
              <span className="h-px w-12 bg-[#c5a059]" />
              <span className="text-[11px] tracking-[0.4em] uppercase text-[#c5a059]">{t.services.label}</span>
            </div>
            <h2 className="font-serif-display font-medium text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-[#f0f4f2]">
              {t.services.title.pre}<span className="italic text-[#c5a059]">{t.services.title.it}</span>{t.services.title.post}
            </h2>
          </div>
          <p className="text-sm text-[#8a9690] font-light max-w-sm leading-relaxed">{t.services.note}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 1 }}>
          {SERVICES.map((s) => (
            <ServiceRow
              key={s.id}
              service={s}
              tr={t.services.items[s.id]}
              t={t}
              isOpen={open === s.id}
              onToggle={() => handleToggle(s.id)}
              rowRef={(el) => (rowRefs.current[s.id] = el)}
            />
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-20"
        >
          <TiltCard
            testid="addons-block"
            max={3}
            lift={5}
            glow="0 24px 60px -20px rgba(197,160,89,0.45)"
            className="border border-[#c5a059]/25 bg-[#0c100e]/60 p-8 lg:p-12"
          >
            <div className="flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-16">
              <div className="lg:w-1/3">
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#c5a059]">{t.services.addons.label}</span>
                <h3 className="mt-3 font-serif-display text-3xl text-[#f0f4f2]">
                  {t.services.addons.title} <span className="italic text-[#c5a059]">{t.services.addons.italic}</span>
                </h3>
                <p className="mt-4 text-sm text-[#8a9690] font-light leading-relaxed">{t.services.addons.note}</p>
              </div>
              <div className="flex-1 grid sm:grid-cols-2 gap-x-12">
                {ADD_ONS.map((a, i) => (
                  <div key={a.name} className="flex items-baseline gap-3 py-3 border-b border-white/10">
                    <span className="text-sm text-[#f0f4f2]/85 font-light">{t.services.addons.items[i]}</span>
                    <span className="flex-1 border-b border-dotted border-white/15 -translate-y-1" />
                    <span className="font-serif-display text-xl text-[#c5a059]">{a.price}</span>
                  </div>
                ))}
              </div>
            </div>
          </TiltCard>
        </motion.div>
      </div>
    </section>
  );
};
